import React from 'react';
import styled, { css } from 'styled-components';
import { ImageIntentContext } from '../../../../utils/imageIntentReporter';
import type { ImageIntentGallery } from './imageIntentGallery';
import ImageProtectionOverlay from './ImageProtectionOverlay';
import useImageIntentFriction from './useImageIntentFriction';

const Wrapper = styled.div<{ $protected: boolean }>`
  position: relative;
  display: block;

  ${(props) =>
    props.$protected &&
    css`
      img {
        user-select: none;
      }
    `}
`;

interface ProtectedImageProps extends ImageIntentContext {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  imageSrc?: string;
  // A pooled image's own gallery (imageIntentGalleryOf), spread over the
  // context like the other call sites do.
  intentGallery?: ImageIntentGallery | Record<string, never>;
}

// Convenience wrapper for sites where the image can safely gain one extra
// container div: renders the vignette overlay above the children and wires
// the save-friction/telemetry handlers. For sites where a wrapper would break
// layout (absolutely-positioned images, styled grids), compose
// ImageProtectionOverlay + useImageIntentFriction directly instead.
//
// The gallery props (galleryKey / picsGalleryId / dp2GalleryId, or a pooled
// image's intentGallery) pass through to getFrictionProps untouched, so
// wrapping an image never changes its attribution: give none and the hook's
// Redux gallery applies, give any and they are used as a unit.
const ProtectedImage = ({
  children,
  className,
  style,
  galleryKey,
  picsGalleryId,
  dp2GalleryId,
  intentGallery,
  picsImageId,
  imageKey,
  surface,
  metadata,
  imageSrc,
}: ProtectedImageProps) => {
  const { protectionEnabled, getFrictionProps } = useImageIntentFriction();
  const context: ImageIntentContext = {
    galleryKey,
    picsGalleryId,
    dp2GalleryId,
    picsImageId,
    imageKey,
    surface,
    metadata,
    ...intentGallery,
  };

  return (
    <Wrapper
      className={className}
      style={style}
      $protected={protectionEnabled}
      {...getFrictionProps(context)}
    >
      {children}
      <ImageProtectionOverlay context={context} imageSrc={imageSrc} />
    </Wrapper>
  );
};

export default ProtectedImage;
